import { Navigate } from 'react-router-dom';
import { AdminLayout } from "~/layout";
import Home from "~/features/Home/pages";
import PageNotFound from "../features/NotFound/pages";
import Account from "~/features/Account/pages/Account";
import Permission from "~/features/Permission/pages/Permission";
import SeoCheck from "~/features/SeoCheck/pages/SeoCheck";
import SeoHistory from "~/features/SeoCheck/pages/SeoHistory";
import PageSpeed from "~/features/PageSpeed/pages/PageSpeed";
import PageSpeedHistory from "~/features/PageSpeed/pages/PageSpeedHistory";
import TrendsHome from "~/features/GoogleTrends/pages/TrendsHome";
import SchemaGenerator from "~/features/SchemaTool/pages/SchemaGenerator";
import SchemaValidator from "~/features/SchemaTool/pages/SchemaValidator";
import SchemaSaved from "~/features/SchemaTool/pages/SchemaSaved";
import ProjectList from "~/features/Project/pages/ProjectList";
import ProjectDashboard from "~/features/Project/pages/ProjectDashboard";
import CrawlerHome from "~/features/SiteCrawler/pages/CrawlerHome";
import CrawlReport from "~/features/SiteCrawler/pages/CrawlReport";
import CrawlPageDetail from "~/features/SiteCrawler/pages/CrawlPageDetail";
import CrawlCompare from "~/features/SiteCrawler/pages/CrawlCompare";
import GscHome from "~/features/GscInsight/pages/GscHome";
import GscCallback from "~/features/GscInsight/pages/GscCallback";
import GaHome from "~/features/GaInsight/pages/GaHome";
import KeywordHome from "~/features/KeywordResearch/pages/KeywordHome";
import RankHome from "~/features/RankTracker/pages/RankHome";
import BacklinkHome from "~/features/Backlinks/pages/BacklinkHome";
import CompetitorHome from "~/features/CompetitorAnalysis/pages/CompetitorHome";
import TechnicalHome from "~/features/Technical/pages/TechnicalHome";
import AiTool from "~/features/AiTool/pages/AiTool";
import ContentHome from "~/features/ContentAi/pages/ContentHome";
import AiHistory from "~/features/AiTool/pages/AiHistory";
import AiProjectHome from "~/features/AiTool/pages/AiProjectHome";
import SubscriptionPage from "~/features/Subscription/pages/SubscriptionPage";
import GlossaryPage from "~/features/Glossary/pages/GlossaryPage";
import ApiAccessPage from "~/features/ApiAccess/pages/ApiAccessPage";
import MemberList from "~/features/Member/pages/MemberList";
import MemberDetail from "~/features/Member/pages/MemberDetail";
import PlanManager from "~/features/Member/pages/PlanManager";
import AdminStats from "~/features/Member/pages/AdminStats";
import PlanRequests from "~/features/Member/pages/PlanRequests";
import ExtraPurchases from "~/features/Member/pages/ExtraPurchases";
import GoogleAdsSettings from "~/features/Member/pages/GoogleAdsSettings";
import ServiceQuotas from "~/features/Member/pages/ServiceQuotas";

// Quyền cho phép truy cập cụm Dự án (giống điều kiện hiện menu ở NavBarData).
const PROJECT_CAPS = ['project', 'crawl', 'gsc', 'ga', 'ai', 'keyword', 'competitor'];

/**
 * `cap`: quyền yêu cầu để vào route (string hoặc mảng = có 1 trong số đó; root luôn qua).
 * Không khai báo = ai đăng nhập cũng vào được. App.js bọc <RequireCap> khi route có cap
 * → không có quyền thì hiện màn NoPermission thay vì vào trang rồi nhận lỗi API.
 */
export const privateRoutes = [
	{ path: "/", component: Home },
	{ path: "*", component: PageNotFound },
	// Hồ sơ cá nhân — 1 trang /account với tabs (info | password)
	{ path: "/account", component: Account },
	// Route cũ → redirect về /account (giữ link/bookmark không gãy)
	{ path: "/profile", component: () => <Navigate to={process.env.REACT_APP_HOMEPAGE + "/account"} replace /> },
	{ path: "/account/password", component: () => <Navigate to={process.env.REACT_APP_HOMEPAGE + "/account?tab=password"} replace /> },
	// Route cũ → redirect sang khu quản trị mới
	{ path: "/permission", component: () => <Navigate to={process.env.REACT_APP_HOMEPAGE + "/admin/permission"} replace /> },
	// Chấm điểm SEO
	{ path: "/seo", component: SeoCheck, cap: 'seo' },
	{ path: "/seo/history", component: SeoHistory, cap: 'seo' },
	// PageSpeed / Core Web Vitals
	{ path: "/pagespeed", component: PageSpeed, cap: 'pagespeed' },
	{ path: "/pagespeed/history", component: PageSpeedHistory, cap: 'pagespeed' },
	// Google Trends — xu hướng tìm kiếm theo quốc gia (lịch sử là tab trong trang)
	{ path: "/trends", component: TrendsHome, cap: 'trends' },
	// AI SEO (độc lập)
	{ path: "/ai", component: AiTool, cap: 'ai' },
	{ path: "/ai/history", component: AiHistory, cap: 'ai' },
	// Schema.org Generator + Validator
	{ path: "/schema/generator", component: SchemaGenerator, cap: 'schema' },
	{ path: "/schema/validator", component: SchemaValidator, cap: 'schema' },
	{ path: "/schema/saved", component: SchemaSaved, cap: 'schema' },
	// Dự án (thực thể tổng) + công cụ bên trong
	{ path: "/projects", component: ProjectList, cap: PROJECT_CAPS },
	{ path: "/projects/:id", component: ProjectDashboard, cap: PROJECT_CAPS },
	// Tool Site Crawler trong 1 dự án
	{ path: "/projects/:id/crawler", component: CrawlerHome, cap: 'crawl' },
	{ path: "/crawler/crawls/:id", component: CrawlReport, cap: 'crawl' },
	{ path: "/crawler/compare/:a/:b", component: CrawlCompare, cap: 'crawl' },
	{ path: "/crawler/pages/:id", component: CrawlPageDetail, cap: 'crawl' },
	// Tool Kỹ thuật SEO trong 1 dự án (báo cáo kỹ thuật từ lần crawl mới nhất)
	{ path: "/projects/:id/technical", component: TechnicalHome, cap: 'crawl' },
	// Tool Google Search Console trong 1 dự án + đích redirect OAuth (dùng chung với GA4
	// và Google Ads admin — state tiền tố 'ads.' cần cap member_manage)
	{ path: "/projects/:id/gsc", component: GscHome, cap: 'gsc' },
	{ path: "/gsc/oauth/callback", component: GscCallback, cap: ['gsc', 'ga', 'member_manage'] },
	// Tool Google Analytics 4 trong 1 dự án
	{ path: "/projects/:id/analytics", component: GaHome, cap: 'ga' },
	// Tool Nghiên cứu từ khoá trong 1 dự án (#5 — GSC explorer + kho từ khoá)
	{ path: "/projects/:id/keywords", component: KeywordHome, cap: 'keyword' },
	// Tool Theo dõi thứ hạng SERP trong 1 dự án (#6 — vị trí thật Desktop/Mobile + quốc gia qua DataForSEO)
	{ path: "/projects/:id/rank", component: RankHome, cap: 'keyword' },
	// Tool Backlink trong 1 dự án (#9 — DataForSEO Backlinks API, cache dùng chung 7 ngày)
	{ path: "/projects/:id/backlinks", component: BacklinkHome, cap: 'keyword' },
	// Tool Phân tích đối thủ trong 1 dự án (#21 — so sánh SEO + content gap + độ phủ từ khoá)
	{ path: "/projects/:id/competitor", component: CompetitorHome, cap: 'competitor' },
	// Tool AI SEO trong 1 dự án (Trợ lý AI phân tích dữ liệu + Sinh nội dung)
	{ path: "/projects/:id/ai", component: AiProjectHome, cap: 'ai' },
	// Tool Nội dung AI tự động trong 1 dự án (§9 — pipeline dàn ý/viết/ảnh/checklist chạy nền)
	{ path: "/projects/:id/content", component: ContentHome, cap: 'ai' },
	// Gói & Hạn mức của chính user (mọi user đều xem được gói của mình)
	{ path: "/subscription", component: SubscriptionPage },
	// Thuật ngữ SEO — từ điển giải thích cho người mới (ai đăng nhập cũng xem được)
	{ path: "/glossary", component: GlossaryPage },
	// API & Webhook (#25 — "API hệ thống"): quản lý API key + webhook + tài liệu.
	// Không cần cap; thao tác tạo/sửa gate theo cờ gói can_use_api (402 từ BE).
	{ path: "/api-access", component: ApiAccessPage },
	// ===== CHẾ ĐỘ QUẢN TRỊ (/admin/*) — layout AdminLayout với sidebar riêng =====
	{ path: "/admin", component: () => <Navigate to={process.env.REACT_APP_HOMEPAGE + "/admin/stats"} replace />, layout: AdminLayout },
	{ path: "/admin/stats", component: AdminStats, cap: 'member_manage', layout: AdminLayout },
	{ path: "/admin/members", component: MemberList, cap: 'member_manage', layout: AdminLayout },
	{ path: "/admin/members/:id", component: MemberDetail, cap: 'member_manage', layout: AdminLayout },
	{ path: "/admin/plan-requests", component: PlanRequests, cap: 'member_manage', layout: AdminLayout },
	{ path: "/admin/extra-purchases", component: ExtraPurchases, cap: 'member_manage', layout: AdminLayout },
	{ path: "/admin/plans", component: PlanManager, cap: 'member_manage', layout: AdminLayout },
	{ path: "/admin/google-ads", component: GoogleAdsSettings, cap: 'member_manage', layout: AdminLayout },
	// Dịch vụ bên thứ 3 — hạn mức & ngân sách các nhà cung cấp (AI/DataForSEO/Google/Email)
	{ path: "/admin/services", component: ServiceQuotas, cap: 'member_manage', layout: AdminLayout },
	{ path: "/admin/permission", component: Permission, cap: 'permission', layout: AdminLayout },
];

export function PrivateRoutes({children}) {
	// Check if user is logged in
	// If yes, show route
	// Otherwise, redirect to login page
	const isLoggedIn = Boolean(localStorage.getItem('access_token'));

	if (!isLoggedIn) return <Navigate to={process.env.REACT_APP_HOMEPAGE + "/login"} />;

	return children;
}
